import {
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  ProfileInfoApi,
  SessionApi,
  OAuthApi,
  OpenIdConnectApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';

import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { CompositeRadarApi } from './radars/CompositeRadarApi';
import { OAuth2, SamlAuth } from '@backstage/core-app-api';
import {
  scmAuthApiRef,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';

/**
 * Custom ApiRefs for each provider. Keep the interface surface explicit.
 * - OIDC/Auth0 expose OAuth flows + OIDC
 * - SAML exposes session & identity/profile only
 */

// “acme” OAuth provider (OIDC-capable)
export const acmeAuthApiRef: ApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
> = createApiRef({ id: 'auth.acme' });

// Generic OIDC provider
export const oidcAuthApiRef: ApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
> = createApiRef({ id: 'auth.oidc' });

// Auth0 provider
export const auth0AuthApiRef: ApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
> = createApiRef({ id: 'auth.auth0' });

// SAML provider (no OAuth/OIDC)
export const samlAuthApiRef: ApiRef<
  ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.saml' });

export const apis: AnyApiFactory[] = [
  // ---- Auth Providers ----

  // Acme OAuth2/OIDC
  createApiFactory({
    api: acmeAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        environment: configApi.getOptionalString('auth.environment'),
        // If Acme uses a custom provider id/title/icon, set it here:
        provider: {
          id: 'acme',
          title: 'Acme SSO',
          icon: () => null,
        },
      }),
  }),

  // SCM Integrations registry for UI components (e.g., AboutCard)
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),

  // ScmAuth API (minimal stub). This satisfies catalog-import dependency without
  // requiring GitHub/GitLab/Azure/Bitbucket OAuth providers. If SCM OAuth is
  // needed later, replace this with ScmAuth.createDefaultApiFactory() and
  // register the corresponding provider auth APIs.
  createApiFactory({
    api: scmAuthApiRef,
    deps: {},
    factory: () => ({
      async getCredentials() {
        return { token: '', headers: {} } as any;
      },
    }),
  }),

  // OIDC provider
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'oidc',
          title: 'Your Identity Provider',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),

  // Auth0 provider
  createApiFactory({
    api: auth0AuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'auth0',
          title: 'Auth0',
          icon: () => null,
        },
        defaultScopes: ['openid', 'email', 'profile', 'offline_access'],
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),

  // SAML provider
  createApiFactory({
    api: samlAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, configApi }) =>
      SamlAuth.create({
        configApi,
        discoveryApi,
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),

  // ---- Tech Radar override ----
  createApiFactory({
    api: techRadarApiRef,
    deps: {
      catalog: catalogApiRef,
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ catalog, discoveryApi, fetchApi, identityApi }) =>
      new CompositeRadarApi({
        catalog,
        discoveryApi,
        fetchApi,
        identityApi,
      }),
  }),
];
